import { defineStore } from 'pinia'
import { AxiosError } from 'axios'
import { jwtDecode } from 'jwt-decode'

import { InputLoginCredential, InputRegisterUser, InputTwoFactorCode, AuthResponse } from '../../services/auth/types'
import { API } from '../../services'
import { APIResponse } from '../../services/api/types'
import { AuthState, JwtPayload } from './types'
import { InputUpdateUser, User } from '../../services/user/types'
import logger from '../../services/logger'

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    loading: false,
    userProfile: null as User | null,
    access_token: null,
    refresh_token: null,
    twoFactorSecret: null,
    showModal: false,
  }),
  actions: {
    setTokens(tokens: AuthResponse): void {
      this.access_token = tokens.access_token
      this.refresh_token = tokens.refresh_token
    },
    setUserProfile(profile: User): void {
      this.userProfile = profile
    },
    async register(input: InputRegisterUser): Promise<APIResponse<string>> {
      this.loading = true
      try {
        const { status, data } = await API.auth.registerUser(input)
        if (status === 201) {
          this.setTokens(data)
          this.loading = false
          return {
            success: true,
            content: '',
          }
        }
      } catch (error) {
        const _error = error as AxiosError<string>
        logger.error(JSON.stringify(error))
        this.loading = false
        return {
          success: false,
          status: _error.response?.status,
          content:
            (_error.response?.data as any)?.message ??
            'Il y a une erreur lors du création de votre compte, veuillez nous contacter par yann@digidocteur.com',
        }
      }
      this.loading = false
      return {
        success: false,
        content: '',
        status: 400,
      }
    },
    async login(input: InputLoginCredential): Promise<APIResponse<string>> {
      this.loading = true
      try {
        const { status, data } = await API.auth.loginUser(input)
        if (status === 200) {
          this.setTokens(data)
          this.loading = false
          return {
            success: true,
            content: '',
          }
        }
      } catch (error) {
        const _error = error as AxiosError<string>
        logger.error(JSON.stringify(error))
        this.loading = false
        return {
          success: false,
          status: _error.response?.status,
          content:
            (_error.response?.data as any)?.message ??
            'Il y a une erreur lors de votre authentification à votre compte, veuillez nous contacter par yann@digidocteur.com',
        }
      }
      this.loading = false
      return {
        success: false,
        content: '',
        status: 400,
      }
    },
    async verify2FA(input: InputTwoFactorCode): Promise<APIResponse<string>> {
      this.loading = true
      try {
        const { status, data } = await API.auth.verify2FA(input)
        if (status === 200) {
          this.setTokens(data)
          this.loading = false
          return {
            success: true,
            content: '',
          }
        }
      } catch (error) {
        const _error = error as AxiosError<string>
        this.loading = false
        return {
          success: false,
          status: _error.response?.status,
          content: (_error.response?.data as any)?.message,
        }
      }
      this.loading = false
      return {
        success: false,
        content: '',
        status: 400,
      }
    },
    logout() {
      this.$reset()
      localStorage.removeItem('auth')
    },
    async generate2FA() {
      this.loading = true
      try {
        const { status, data } = await API.auth.generate2FA()
        if (status === 200) {
          this.twoFactorSecret = data
          this.loading = false
          return {
            success: true,
            content: null,
          }
        }
      } catch (error) {
        const _error = error as AxiosError<string>
        this.loading = false
        return {
          success: false,
          status: _error.response?.status,
          content: null,
        }
      }
      this.loading = false
      return {
        success: false,
        content: null,
        status: 400,
      }
    },
    async enable2FA(input: InputTwoFactorCode): Promise<APIResponse<null>> {
      this.loading = true
      try {
        const { status, data } = await API.auth.enable2FA(input)
        if (status === 200) {
          this.setUserProfile(data)
          this.twoFactorSecret = null
          this.loading = false
          return {
            success: true,
            content: null,
          }
        }
      } catch (error) {
        const _error = error as AxiosError<string>
        this.loading = false
        return {
          success: false,
          status: _error.response?.status,
          content: null,
        }
      }
      this.loading = false
      return {
        success: false,
        content: null,
        status: 400,
      }
    },
    async disable2FA() {
      this.loading = true
      try {
        const { status, data } = await API.auth.disable2FA()
        if (status === 200) {
          this.setUserProfile(data)
          this.loading = false
          return {
            success: true,
            content: null,
          }
        }
      } catch (error) {
        const _error = error as AxiosError<string>
        this.loading = false
        return {
          success: false,
          status: _error.response?.status,
          content: null,
        }
      }
      this.loading = false
      return {
        success: false,
        content: null,
        status: 400,
      }
    },
    async emailConfirmation(): Promise<APIResponse<null>> {
      this.loading = true
      try {
        const { status } = await API.auth.emailConfirmation()
        if (status === 200) {
          this.loading = false
          return {
            success: true,
            content: null,
          }
        }
      } catch (error) {
        const _error = error as AxiosError<string>
        this.loading = false
        return {
          success: false,
          status: _error.response?.status,
          content: null,
        }
      }
      this.loading = false
      return {
        success: false,
        content: null,
        status: 400,
      }
    },
    async forgotPassword(email: string): Promise<APIResponse<null>> {
      this.loading = true
      try {
        const { status } = await API.auth.requestResetPassword(email)
        if (status === 200) {
          this.loading = false
          return {
            success: true,
            content: null,
          }
        }
      } catch (error) {
        const _error = error as AxiosError<string>
        this.loading = false
        return {
          success: false,
          status: _error.response?.status,
          content: null,
        }
      }
      this.loading = false
      return {
        success: false,
        content: null,
        status: 400,
      }
    },
    async getUserProfile(): Promise<void> {
      this.loading = true
      try {
        const { status, data } = await API.auth.getProfile()
        if (status === 200) {
          this.setUserProfile(data)
        }
      } catch (error) {
        /* empty */
      }
      this.loading = false
    },
    async updateUserProfile(input: InputUpdateUser): Promise<APIResponse<null>> {
      this.loading = true
      try {
        const { status, data } = await API.auth.updateProfile(input)
        if (status === 200) {
          this.setUserProfile(data)
          this.loading = false
          return {
            success: true,
            content: null,
          }
        }
      } catch (error) {
        const _error = error as AxiosError<string>
        this.loading = false
        return {
          success: false,
          status: _error.response?.status,
          content: null,
        }
      }
      this.loading = false
      return {
        success: false,
        content: null,
        status: 400,
      }
    },
    async updateUserAvatar(file: File): Promise<APIResponse<null>> {
      this.loading = true
      try {
        const formData = new FormData()
        formData.append('file', file)

        const { status, data } = await API.auth.updateAvatar(formData)
        if (status === 200) {
          this.setUserProfile({ ...this.userProfile, avatar: data.url } as User)
          this.loading = false
          return {
            success: true,
            content: null,
          }
        }
      } catch (error) {
        const _error = error as AxiosError<string>
        this.loading = false
        return {
          success: false,
          status: _error.response?.status,
          content: null,
        }
      }
      this.loading = false
      return {
        success: false,
        content: null,
        status: 400,
      }
    },
  },
  getters: {
    needLogin: (state) => !(state.access_token || state.refresh_token), // Vérifie si l'utilisateur a besoin de s'identifier
    require2FA: (state) => {
      if (!state.access_token && !state.refresh_token) return false
      const decoded: JwtPayload = jwtDecode(state.access_token as string)
      const { is2fa_required, is2fa_verified } = decoded
      return is2fa_required && !is2fa_verified
    }, // Vérifie si l'utilisateur a besoin de 2FA
    isAuthenticated: (state) => {
      if (!state.access_token && !state.refresh_token) return false
      const decoded: JwtPayload = jwtDecode(state.access_token as string)

      const { is2fa_required, is2fa_verified } = decoded
      return !is2fa_required || is2fa_verified
    }, // Vérifie si l'utilisateur est connecté
    isAdmin: (state) => state.userProfile?.role === 'ADMIN',
    isActiveUser: (state) => state.userProfile?.status === 'ACTIVE', // Vérifie si l'utilisateur est ACTIVE
    is2FAEnabled: (state) => state.userProfile?.is2FAEnabled, // Vérifie si l'utilisateur a 2FA activé
    isPendingUser: (state) => !state.userProfile?.accountAttribution || state.userProfile?.accountAttribution != 'YES', // Vérifie si l'utilisateur est PENDING
  },
  persist: {
    key: 'auth', // Le nom de la clé dans le localStorage pour persister les données
    pick: ['access_token', 'refresh_token'], // Les données à persister
  },
})
